// Kanban board — one column per `ticket_columns` row, each rendering the
// tickets that belong to it via getTicketsForColumn(). Drag-and-drop is
// HTML5-native (draggable + dragstart/over/drop events) — keeping this
// minimal until we need touch support.

import { getTicketsForColumn, githubColorToken, positionBetween } from '../../functions/tickets/ticketUtils.js'

export const TicketsBoardView = {
  flow: 'x',
  flex: '1',
  overflow: 'auto',
  padding: 'A1 A1 A1 A',
  gap: 'A',
  align: 'stretch left',
  attr: { 'data-tickets-board': 'true' },

  childExtends: 'TicketsBoardColumn',
  childrenAs: 'state',
  // Pull the columns straight from root.columns — they were mirrored from
  // GitHub's Status field options on the last sync pass, so the board's
  // shape (count, order, names, colours) matches the upstream project 1:1.
  children: (el, s) => (s.root.columns || []).slice().sort((a, b) => a.position - b.position)
}

export const TicketsBoardColumn = {
  flow: 'y',
  width: 'I',
  minWidth: 'I',
  height: '100%',
  minHeight: '0',
  background: 'gray.05',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  round: 'Z1',
  overflow: 'hidden',

  // Drop target — drop a ticket card on the column body and we move it to
  // the end of this column. Per-card insertion happens on the card itself.
  attr: { 'data-col-key': (el, s) => s.key },
  onDragover: (e, el, s) => { e.preventDefault() },
  onDrop: async (e, el, s) => {
    e.preventDefault()
    const ticketId = Number(e.dataTransfer.getData('text/ticket-id'))
    if (!ticketId) return
    const root = el.state.root
    const colKey = s.key
    const inCol = (root.tickets || []).filter(t => t.columnKey === colKey)
    const lastPos = inCol.length ? Math.max(...inCol.map(t => t.position || 0)) : 0
    const nextPos = positionBetween(lastPos, null)
    root.update({
      tickets: (root.tickets || []).map(t => t.id === ticketId ? { ...t, columnKey: colKey, position: nextPos } : t)
    })
    el.call('moveTicket', { ticketId, columnKey: colKey, position: nextPos })
  },

  Header: {
    flow: 'x',
    align: 'center space-between',
    padding: 'Z A',

    Left: {
      flow: 'x',
      align: 'center left',
      gap: 'Z',

      Dot: {
        width: 'Z',
        height: 'Z',
        round: '50%',
        background: (el, s) => githubColorToken(s.color),
        boxShadow: (el, s) => `0 0 8px color-mix(in srgb, ${githubColorToken(s.color)} 60%, transparent)`
      },
      Label: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '600',
        letterSpacing: '-.005em',
        color: 'title',
        text: (el, s) => s.label,
        title: (el, s) => s.description || ''
      },
      Count: {
        tag: 'span',
        padding: 'V Y',
        round: 'C',
        fontSize: 'V',
        fontWeight: '600',
        color: 'caption',
        background: 'overlay',
        text: (el, s) => {
          let n = el
          while (n && !Array.isArray(n.state?.tickets)) n = n.parent
          const root = n?.state || {}
          const colKey = el.parent.parent.state?.key
          const list = getTicketsForColumn(root, colKey)
          return String(list.length)
        }
      }
    },

    AddBtn: {
      extends: 'IconButton',
      Icon: { icon: 'plus' },
      onClick: (e, el, s) => {
        el.state.root.update({
          selectedTicket: { _new: true },
          ticketDraft: { title: '', body: '', columnKey: s.key, assigneeEmail: '', labels: [], priority: 'medium', dueDate: '' }
        })
      }
    }
  },

  // Card list — incremental pagination via IntersectionObserver. Each
  // column's bottom Sentinel triggers el.call('pageColumn', colKey) when
  // it scrolls into view, fetching the next PAGE_SIZE rows of just that
  // column from Supabase. Combined with content-visibility:auto on the
  // cards, this keeps initial mount cheap (50 cards × 12 columns = 600
  // total) and only loads more when the user actually scrolls down.
  List: {
    flow: 'y',
    gap: 'Z',
    padding: 'Z A',
    flex: '1 1 0',
    minHeight: '0',
    overflowY: 'auto',
    position: 'relative',

    Items: {
      flow: 'y',
      gap: 'Z',

      childExtends: 'TicketsCard',
      childrenAs: 'state',
      children: (el, s) => {
        let n = el
        while (n && !Array.isArray(n.state?.tickets)) n = n.parent
        const root = n?.state || {}
        return getTicketsForColumn(root, el.parent.parent.state.key)
      }
    },

    // Sentinel — sits at the bottom of the column. When it intersects
    // the column's scroll viewport (rootMargin gives a 200px lookahead
    // so we start loading just before the user reaches the end), call
    // pageColumn to fetch more rows for this column.
    Sentinel: {
      tag: 'div',
      display: (el, s) => {
        let n = el
        while (n && !n.state?.totalByColumn) n = n.parent
        const root = n?.state || {}
        const colKey = el.parent.parent.state.key
        const loaded = (root.loadedByColumn || {})[colKey] || 0
        const total = (root.totalByColumn || {})[colKey] || 0
        return loaded < total ? 'flex' : 'none'
      },
      align: 'center center',
      padding: 'A',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => {
        let n = el
        while (n && !n.state?.totalByColumn) n = n.parent
        const root = n?.state || {}
        const colKey = el.parent.parent.state.key
        const loaded = (root.loadedByColumn || {})[colKey] || 0
        const total = (root.totalByColumn || {})[colKey] || 0
        const loading = (root.loadingColumn || {})[colKey]
        return loading ? 'Loading…' : `Load more · ${loaded} / ${total}`
      },
      onCreate: (el, s) => {
        const colKey = el.parent.parent.state.key
        // root: null = browser viewport. Sentinel only triggers when
        // the column is BOTH horizontally on screen AND its own scroll
        // has reached the bottom (= sentinel becomes visible). The
        // generous rootMargin starts loading just before the user
        // physically reaches the end of the list.
        el._io = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              el.call('pageColumn', colKey)
              break
            }
          }
        }, { root: null, rootMargin: '400px 200px', threshold: 0 })
        el._io.observe(el.node)
      },
      onRemove: (el) => {
        if (el._io) el._io.disconnect()
      },
    }
  }
}
